import PropTypes from "prop-types";
import React, { Component } from "react";
import ImageIcon from "components/icons/image";
import superagent from "superagent";
import theme from "styles/theme";
import { merge, $ } from "glamor";
import Button from "components/form/button";
import Globalize from "globalize";
import Promise from "bluebird";
import { DropzoneUploader } from "components/upload/dropzone-uploader";
import { LogoTypes } from "components/management/custom-branding/image-uploader";

const styles = {
    specList: {
        textAlign: "left",
    },
    uploadIconBase: {
        width: "100px",
        height: "100px",
        fill: theme.palette.borderColor,
        position: "absolute",
        left: "25%",
        top: "-15px",
    },
    spinner: {
        position: "absolute",
        width: "40px",
        height: "40px",
    },
    button: {
        backgroundColor: theme.palette.lighterGrey,
        position: "relative",
        top: "10px",
    },
};

const altStyles = merge(
    $(" .specificationList", {
        textAlign: "left",
        width: "280px",
        minWidth: "240px",
        margin: "0 0 0 30px",
    }),
    $(".isMobile & .specificationList", {
        minWidth: "300px",
        width: "300px",
        margin: "0 0 0 30px",
    }),
    $(" .specificationList p", {
        fontSize: "12px",
    }),
    $(" .specificationList li", {
        fontSize: "12px",
    }),
    $(" .uploadInstructions", {
        display: "flex",
        flexWrap: "wrap",
        minWidth: "150px",
        margin: "0px auto",
        padding: "60px 0px 0px 0px",
        alignItems: "center",
        justifyContent: "center",
    }),
    $(" .withSpecs .uploadInstructionsContent", {
        width: "320px",
    }),
    $(" .withSpecs .uploadInstructionsContent p", {
        fontSize: "20px",
        marginTop: "25px",
    }),
    $(" .uploadInstructionsContent", {
        textAlign: "center",
        width: "200px",
        height: "100px",
        position: "relative",
    }),
    $(".isMobile & .uploadInstructionsContent", {
        minWidth: "300px",
        width: "300px",
    }),
    $(" .uploadInstructionsContent p", {
        position: "relative",
        zIndex: 5,
        lineHeight: "20px",
    }),
    $(" .uploadInstructionsContent p", {
        color: theme.palette.textColor,
        fontSize: "14px",
    }),
    $(" .uploadInstructionsContent .restrictions", {
        color: theme.palette.textColor,
        fontSize: "12px",
        lineHeight: "20px",
    }),
    $(" .bullet", {
        color: theme.palette.primary1Color,
        margin: "0 6px 0 0",
    }),
);

const OCTET_STREAM_CONTENT_TYPE = "application/octet-stream";

/** @augments {Component<any, any>} */
class DirectUploader extends Component {
    static propTypes = {
        uploadUrl: PropTypes.string.isRequired,
        uploadPath: PropTypes.string,
        width: PropTypes.string,
        height: PropTypes.string,
        logoType: PropTypes.string,
        uploadCallback: PropTypes.func,
        onFilesSelected: PropTypes.func,
        multiple: PropTypes.bool,
        minFiles: PropTypes.number,
        maxFiles: PropTypes.number,
        maxSize: PropTypes.number,
        minSize: PropTypes.number,
        minWidth: PropTypes.number,
        maxWidth: PropTypes.number,
        minHeight: PropTypes.number,
        maxHeight: PropTypes.number,
        aspectRatio: PropTypes.oneOf(["square", "custom"]),
        aspectRatioH: PropTypes.number,
        aspectRatioV: PropTypes.number,
        children: PropTypes.any,
        accept: PropTypes.string, //All MIME types that this uploader will accept, eg. "image/jpeg, image/png",
        acceptInstructions: PropTypes.string, //eg. "Only accepts png, jpeg"
        uploadInstructions: PropTypes.string,
        uploadInstructionsStyle: PropTypes.object,
        instructionsStyle: PropTypes.object,
        specifications: PropTypes.array,
        specificationListStyle: PropTypes.object,
        style: PropTypes.object,
        spinnerStyle: PropTypes.object,
        onDropReject: PropTypes.func,
        onDrop: PropTypes.func,
        // activeStyle: PropTypes.object.isRequired,
        uploadIconStyle: PropTypes.object,
        onUploadStart: PropTypes.func,
        onUploadError: PropTypes.func.isRequired,
        uploadIcon: PropTypes.node,
        updating: PropTypes.bool,
        onLoading: PropTypes.func,
        showSelectButton: PropTypes.bool.isRequired,
        isLegacy: PropTypes.bool.isRequired,
        scope: PropTypes.object.isRequired,
        defaultError: PropTypes.string,
        maxSizeError: PropTypes.string,
    };

    /*http://www.iana.org/assignments/media-types/media-types.xhtml for full list of MIME Types*/

    static defaultProps = {
        width: "160px",
        height: "160px",
        multiple: false,
        acceptInstructions: "",
        updating: false,
        showSelectButton: true,
        isLegacy: false,
        logoType: "",
    };

    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            uploadIcon: props.uploadIcon ? (
                props.uploadIcon
            ) : (
                <ImageIcon style={Object.assign({}, styles.uploadIconBase, props.uploadIconStyle)} />
            ),
        };
    }

    uploadFile(fileData) {
        const { uploadUrl } = this.props;
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(fileData);
            reader.onload = () => {
                const data = {
                    content_type: fileData.type || OCTET_STREAM_CONTENT_TYPE,
                    filename: fileData.name,
                    filesize_byte: fileData.size,
                };

                superagent
                    .post(uploadUrl)
                    .send(data)
                    .set("x-requested-with", "XMLHttpRequest")
                    .set("Accept", "application/json")
                    .withCredentials()
                    .end((err, response) => {
                        if (err) {
                            return reject({
                                message: Globalize.formatMessage(
                                    "Oops, there was a problem! Please refresh and try again.",
                                ),
                            });
                        }
                        const responseData = response.body.data[0];
                        //Use whatever method & url combo given to use from the upload service
                        superagent
                            .put(responseData.url)
                            .set("content-type", data.content_type)
                            .send(fileData)
                            .end((err) => {
                                if (err) {
                                    return reject({
                                        message: Globalize.formatMessage(
                                            "Oops, there was a problem! Please refresh and try again.",
                                        ),
                                    });
                                }
                                return resolve([responseData, fileData]);
                            });
                    });
            };
        });
    }

    legacyUploadFile(fileData, logoType) {
        const { uploadUrl, scope } = this.props;
        return new Promise((resolve, reject) => {
            const data = new FormData();
            if (logoType) {
                data.append("branding", logoType);
                data.append("asset_0", fileData);
            } else {
                data.append("file", fileData);
            }

            superagent
                .put(uploadUrl)
                .send(data)
                .set("x-requested-with", "XMLHttpRequest")
                .set("Accept", "application/json")
                .set("Active-Brand", scope.brand._id)
                .set("module", logoType === LogoTypes.Linesheets ? "admin" : "default")
                .withCredentials()
                .end((err, response) => {
                    if (err) {
                        const error = new Error(
                            Globalize.formatMessage("Oops, there was a problem! Please refresh and try again."),
                        );
                        reject(error);
                    } else {
                        const json = response.body;
                        //Hack because the endpoint returns a 200 even if an image upload failed.
                        if (
                            json.rejected_image_list &&
                            json.rejected_image_list.length &&
                            json.rejected_image_messages.length
                        ) {
                            reject({
                                message:
                                    json.rejected_image_messages[0] ||
                                    Globalize.formatMessage("There was an error uploading the image."),
                            });
                        } else {
                            resolve([json, fileData]);
                        }
                    }
                });
        });
    }

    handleDrop = (files) => {
        const { multiple, onDrop, onUploadStart, onUploadError, uploadCallback, isLegacy, logoType, onFilesSelected } =
            this.props;
        const trigger = () => {
            this.setLoading(true);
            onUploadStart?.();
            return Promise.map(
                files,
                (fileData) => (isLegacy ? this.legacyUploadFile(fileData, logoType) : this.uploadFile(fileData)),
                { concurrency: 6 },
            )
                .then((results) => (multiple ? uploadCallback(results) : uploadCallback(...results[0])))
                .then(() => onDrop?.(files))
                .catch((err) => onUploadError(err))
                .then(() => this.setLoading(false));
        };

        if (onFilesSelected) {
            onFilesSelected(files, trigger.bind(this));
        } else {
            trigger();
        }
    };

    renderSpecifications() {
        const { specifications, specificationListStyle } = this.props;
        let specs;
        if (specifications) {
            const specificationList = specifications.map((text, i) => {
                return (
                    <li key={i}>
                        <span className="bullet">•</span>
                        {text}
                    </li>
                );
            });

            specs = (
                <div style={Object.assign({}, styles.specList, specificationListStyle)} className="specificationList">
                    <p>{Globalize.formatMessage("Specifications:")}</p>
                    <ul>{specificationList}</ul>
                </div>
            );
        }

        return specs;
    }

    render() {
        const { loading } = this.state;
        const {
            acceptInstructions,
            children,
            uploadInstructions,
            instructionsStyle,
            uploadInstructionsStyle,
            showSelectButton,
        } = this.props;
        const { updating, onLoading } = this.props;

        let childrenContent;
        if (children) {
            childrenContent = children;
        } else {
            childrenContent = (
                <div className="uploadInstructions">
                    <div className="uploadInstructionsContent" style={Object.assign({}, uploadInstructionsStyle)}>
                        {this.state.uploadIcon}
                        <p style={Object.assign({}, instructionsStyle)}>{uploadInstructions}</p>
                        <p className="restrictions">{acceptInstructions}</p>
                        {showSelectButton && (
                            <Button label={Globalize.formatMessage("Select File")} style={styles.button} />
                        )}
                    </div>
                    {this.renderSpecifications()}
                </div>
            );
        }

        if (!onLoading && (loading || updating)) {
            // newer DropzoneUploader component displays spinner
            childrenContent = null;
        }

        return (
            <div {...altStyles} className="uploaderContainer">
                <DropzoneUploader
                    loading={!onLoading && (loading || updating)}
                    onDrop={this.handleDrop}
                    {...this.props}
                >
                    {childrenContent}
                </DropzoneUploader>
            </div>
        );
    }

    setLoading = (loading) => (this.props.onLoading ? this.props.onLoading(loading) : this.setState({ loading }));
}

export default DirectUploader;
